// Saves a two-party transaction through the same external contract API as the multi-party
// wizard: a COMPLEX_CONTRACT holding one MILESTONE_CONTRACT. What differs is who the parties are:
// the signed-in organization is the seller (PROVIDER, the one sender) and the buyer is the
// CUSTOMER. Waffy acts as the broker — it holds the money — but is not a party on the contract:
// no BROKER party is sent (agreed with the backend).
import {
  addContractMilestonesExternal,
  createExternalContractDetails,
  editContractPartiesExternal,
  editExternalContractDetails,
  getExternalContractDetails,
  publishExternalDraftContract,
} from "../../../setup/api/services/contract";
import {
  EFeePayee,
  EPaymentBrand,
  EPaymentType,
  ESenderRole,
  EShipmentType,
  IContractDetailsBody,
  IContractDetailsResponseData,
  IMilestonesPartiesData,
  UserProfile,
} from "../../../setup/api/services/contract/models";
import {
  complexContractBody,
  milestoneContractBody,
} from "../../../setup/util/helpers";
import {
  TWO_PARTY_META_KEY,
  TWO_PARTY_META_VALUE,
} from "../../../setup/util/twoPartyContract";
import { IOrganizations } from "../../../setup/types/Organisation";
import { TBnplProvider, TwoPartyContractInputs } from "./schema";

/** The payment methods the organization offers, as `getOrganizationPaymentMethods` returns them. */
export type TOrganizationPaymentMethods = Partial<
  Record<EPaymentType, EPaymentBrand[]>
>;

type TMilestonePaymentMethods = Parameters<typeof milestoneContractBody>[5];

export const BNPL_PROVIDERS: {
  key: TBnplProvider;
  type: EPaymentType;
  brand: EPaymentBrand;
  labelKey: string;
}[] = [
  {
    key: "tabby",
    type: EPaymentType.TABBY,
    brand: EPaymentBrand.TABBY,
    labelKey: "lbl_tabby",
  },
  {
    key: "tamara",
    type: EPaymentType.TAMARA,
    brand: EPaymentBrand.TAMARA,
    labelKey: "lbl_tamara",
  },
  {
    key: "neo",
    type: EPaymentType.NEO,
    brand: EPaymentBrand.NEO,
    labelKey: "lbl_neo",
  },
];

/**
 * The buyer is offered every non-installment method the organization has, plus the installment
 * providers the seller ticked. The design has no other payment-method choice.
 */
export const buildPaymentMethods = (
  organizationMethods: TOrganizationPaymentMethods,
  installments: TwoPartyContractInputs["installments"]
): TMilestonePaymentMethods => {
  const brandsOf = (type: EPaymentType) => organizationMethods[type] || [];
  const bnpl = (key: TBnplProvider, type: EPaymentType) =>
    installments[key] ? brandsOf(type) : [];

  return {
    cards: brandsOf(EPaymentType.MOYASAR).filter(
      (brand) => brand !== EPaymentBrand.APPLE_PAY
    ),
    apple_pay: brandsOf(EPaymentType.APPLE_PAY),
    bank_transfer: brandsOf(EPaymentType.MANUAL_BANK_TRANSFER),
    stc_pay: brandsOf(EPaymentType.STCPAY).length ? [EPaymentBrand.STCPAY] : [],
    tabby: bnpl("tabby", EPaymentType.TABBY),
    tamara: bnpl("tamara", EPaymentType.TAMARA),
    neo: bnpl("neo", EPaymentType.NEO),
  };
};

/** Every brand the buyer can pay with — the set the fee preview takes its maximum over. */
export const offeredBrands = (
  paymentMethods: TMilestonePaymentMethods
): EPaymentBrand[] => [
  ...paymentMethods.cards,
  ...(paymentMethods.cards.length || paymentMethods.apple_pay.length
    ? [EPaymentBrand.APPLE_PAY]
    : []),
  ...(paymentMethods.bank_transfer.length ? [EPaymentBrand.BANK_TRANSFER] : []),
  ...paymentMethods.stc_pay,
  ...paymentMethods.tabby,
  ...paymentMethods.tamara,
  ...paymentMethods.neo,
];

const withTwoPartyMeta = <T extends Partial<IContractDetailsBody>>(
  body: T
): T => ({
  ...body,
  metaData: { ...body.metaData, [TWO_PARTY_META_KEY]: TWO_PARTY_META_VALUE },
});

const projectBody = (values: TwoPartyContractInputs, draft = true) =>
  withTwoPartyMeta(
    complexContractBody(
      ESenderRole.PROVIDER,
      values.title.trim(),
      values.description,
      EShipmentType.NONE,
      draft,
      undefined,
      undefined,
      values.installments.tabby,
      values.installments.neo,
      values.installments.tamara
    )
  );

const milestoneBody = (
  values: TwoPartyContractInputs,
  paymentMethods: TMilestonePaymentMethods,
  currentStep: number,
  draft = true
): IContractDetailsBody => {
  const body = milestoneContractBody(
    ESenderRole.PROVIDER,
    values.title.trim(),
    values.files.filter((file) => !file.endsWith("pdf")),
    values.files.filter((file) => file.endsWith("pdf")),
    values.amount || 0,
    paymentMethods,
    "",
    undefined,
    undefined,
    draft,
    undefined,
    undefined,
    `${currentStep}`
  );
  return withTwoPartyMeta({
    ...body,
    itemDetail: { ...body.itemDetail, description: values.description },
    // Waffy's fee comes out of the seller's amount, as the fee summary tells the seller.
    feePayee: EFeePayee.PROVIDER,
  });
};

export interface IDraftIds {
  complexId?: string;
  milestoneId?: string;
}

/** Step one: creates the draft project and its milestone, or updates them. */
export const saveDetails = async (
  ids: IDraftIds,
  values: TwoPartyContractInputs,
  paymentMethods: TMilestonePaymentMethods,
  currentStep: number
): Promise<Required<IDraftIds>> => {
  const complexId = ids.complexId
    ? (await editExternalContractDetails(projectBody(values), ids.complexId))
        ?.data?.id || ids.complexId
    : (await createExternalContractDetails(projectBody(values)))?.data?.id;

  const body = milestoneBody(values, paymentMethods, currentStep);
  const milestoneId = ids.milestoneId
    ? (await editExternalContractDetails(body, ids.milestoneId))?.data?.id ||
      ids.milestoneId
    : (
        await addContractMilestonesExternal(complexId, {
          parentContractId: complexId,
          milestones: [body],
        })
      )?.data?.milestones?.[0]?.id;

  if (!complexId || !milestoneId) throw new Error("Draft was not created");
  return { complexId, milestoneId };
};

type TMilestoneParty = IMilestonesPartiesData[string][number];

/**
 * The seller and the buyer, both for the full transaction value. The seller — the signed-in
 * organization's account — is the one and only sender.
 */
export const buildParties = (
  values: TwoPartyContractInputs,
  organization: IOrganizations,
  buyerPhoneNumber: string
): TMilestoneParty[] => {
  const amount = values.amount || 0;
  const { buyer } = values;
  return [
    {
      userId: String(organization.accountUserIdentifier),
      role: ESenderRole.PROVIDER,
      sudoName: organization.name,
      amount,
      isSender: true,
    },
    {
      phoneNumber: buyerPhoneNumber,
      role: ESenderRole.CUSTOMER,
      sudoName: buyer.name.trim(),
      amount,
      isSender: false,
      ...(buyer.isCompany
        ? {
            isCompany: true,
            companyName: buyer.companyName.trim(),
            companyUnifiedNumber: buyer.companyUnifiedNumber.trim(),
          }
        : {}),
    },
  ];
};

/**
 * Replaces the milestone's whole party list. The server adds a sender of its own when the
 * milestone is created (from `senderRole` and the signed-in user); `POST …/draft-parties` keeps
 * it beside ours — two senders — so this uses the project's `PUT …/parties`, which replaces the
 * list, as the multi-party wizard does to drop the broker the server adds.
 */
export const saveParties = (
  ids: Required<IDraftIds>,
  parties: TMilestoneParty[]
) =>
  editContractPartiesExternal(ids.complexId, {
    mileStonesParties: { [ids.milestoneId]: parties },
  });

/** The milestone does not have exactly one sender, the organization; publishing would fail. */
export class TwoPartySenderError extends Error {
  constructor(public senders: Partial<UserProfile>[]) {
    super("The seller must be the only sender");
    this.name = "TwoPartySenderError";
  }
}

/** Reads the saved parties back: exactly one sender, and it is the organization's account. */
const assertSellerIsOnlySender = async (
  milestoneId: string,
  sellerUserId: string
) => {
  const saved = (await getExternalContractDetails(milestoneId))?.data;
  const senders = (saved?.parties || []).filter((party) => party?.isSender);
  const [sender] = senders;
  if (
    senders.length !== 1 ||
    sender?.role !== ESenderRole.PROVIDER ||
    String(sender?.userId) !== sellerUserId
  )
    throw new TwoPartySenderError(senders);
};

/** Step three: saves the value and payment methods, the parties' amounts, then publishes. */
export const saveValueAndPublish = async (
  ids: Required<IDraftIds>,
  values: TwoPartyContractInputs,
  paymentMethods: TMilestonePaymentMethods,
  parties: TMilestoneParty[],
  currentStep: number
): Promise<IContractDetailsResponseData> => {
  await editExternalContractDetails(projectBody(values), ids.complexId);
  await editExternalContractDetails(
    milestoneBody(values, paymentMethods, currentStep),
    ids.milestoneId
  );
  await saveParties(ids, parties);
  await assertSellerIsOnlySender(ids.milestoneId, String(parties[0].userId));
  await publishExternalDraftContract(ids.milestoneId);
  return (await getExternalContractDetails(ids.milestoneId))?.data;
};

/**
 * Edits a published transaction the buyer has not paid yet (WAF-3235): the project, the
 * milestone (no longer a draft) and its parties, in the same calls the multi-party wizard uses
 * to edit a created milestone. Nothing is published again.
 */
export const saveEdit = async (
  ids: Required<IDraftIds>,
  values: TwoPartyContractInputs,
  paymentMethods: TMilestonePaymentMethods,
  parties: TMilestoneParty[]
) => {
  await editExternalContractDetails(projectBody(values, false), ids.complexId);
  await editExternalContractDetails(
    milestoneBody(values, paymentMethods, 3, false),
    ids.milestoneId
  );
  await saveParties(ids, parties);
  await assertSellerIsOnlySender(ids.milestoneId, String(parties[0].userId));
};

/** Saves whatever the current step holds, for "save and continue later". */
export const saveProgress = async (
  ids: IDraftIds,
  values: TwoPartyContractInputs,
  paymentMethods: TMilestonePaymentMethods,
  parties: TMilestoneParty[] | undefined,
  currentStep: number
): Promise<Required<IDraftIds>> => {
  const saved = await saveDetails(ids, values, paymentMethods, currentStep);
  if (parties) await saveParties(saved, parties);
  return saved;
};

/** A saved draft, for resuming it in the wizard. */
export const loadDraft = async (complexId: string, milestoneId: string) => {
  const [complex, milestone] = await Promise.all([
    getExternalContractDetails(complexId),
    getExternalContractDetails(milestoneId),
  ]);
  return { complex: complex?.data, milestone: milestone?.data };
};
